import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";

const ROLE_LABELS = {
  seg:      "RevXcel (SEG)",
  admin:    "Admin",
  core:     "Core User",
  readonly: "Read Only",
};

export default function AcceptInvite({ onAccepted }) {
  const [invite,   setInvite]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [step,     setStep]     = useState("loading"); // loading | review | signup | login | accepting | done
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [name,     setName]     = useState("");
  const [working,  setWorking]  = useState(false);

  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (!token) { setError("Invalid invite link."); setLoading(false); return; }
    fetch(`/api/invite?action=lookup&token=${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.error) { setError(data.error); setStep("error"); }
        else { setInvite(data); setEmail(data.email); setStep("review"); }
        setLoading(false);
      })
      .catch(() => { setError("Failed to load invite."); setLoading(false); });
  }, [token]);

  const acceptWithExistingAccount = async () => {
    setWorking(true); setError(null);
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    if (err) { setError(err.message); setWorking(false); return; }

    const res = await fetch("/api/invite?action=accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, user_id: data.session.user.id }),
    });
    const result = await res.json();
    setWorking(false);
    if (!res.ok) return setError(result.error || "Failed to accept invite");
    setStep("done");
    setTimeout(() => {
      window.location.href = "/";
    }, 2000);
  };

  const acceptWithNewAccount = async () => {
    if (!name || !password) return setError("Name and password required");
    if (password.length < 8) return setError("Password must be at least 8 characters");
    setWorking(true); setError(null);

    // Server creates the user via admin API (bypasses client-side duplicate restrictions)
    // then adds them to the org and marks invite accepted — all in one call
    const res = await fetch("/api/invite?action=accept", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, email, password, full_name: name }),
    });
    const result = await res.json();

    if (!res.ok) { setError(result.error || "Failed to accept invite"); setWorking(false); return; }

    // Now sign in with the credentials
    const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
    setWorking(false);
    if (signInErr) { setError(signInErr.message); return; }

    setStep("done");
    setTimeout(() => { window.location.href = "/"; }, 2000);
  };

  const inp = {
    width: "100%", padding: "11px 14px", borderRadius: 8,
    border: "1px solid rgba(0,0,0,0.12)", background: "#F8F8F8",
    fontSize: 13, color: "#111", outline: "none", boxSizing: "border-box",
    fontFamily: "'DM Sans', sans-serif",
  };
  const btn = (bg = "#CC1A1A") => ({
    width: "100%", padding: "12px", borderRadius: 8, border: "none",
    background: working ? "#aaa" : bg, color: "#fff", fontSize: 13,
    fontWeight: 700, cursor: working ? "not-allowed" : "pointer",
    fontFamily: "'DM Sans', sans-serif", marginTop: 8,
  });

  return (
    <div style={{ minHeight: "100vh", background: "#F8F8F8", display: "flex",
      alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');`}</style>

      <div style={{ width: "100%", maxWidth: 420, padding: "0 20px" }}>
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 52, height: 52, background: "#CC1A1A", borderRadius: 13, marginBottom: 12,
            boxShadow: "0 4px 20px rgba(204,26,26,0.3)" }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="16 7 22 7 22 13" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: "#111" }}>RevXcel</div>
        </div>

        <div style={{ background: "#fff", borderRadius: 16, padding: "28px",
          boxShadow: "0 2px 24px rgba(0,0,0,0.08)", border: "1px solid rgba(0,0,0,0.06)" }}>

          {step === "loading" && (
            <div style={{ textAlign: "center", padding: "20px 0", color: "#aaa", fontSize: 13 }}>
              Loading invite…
            </div>
          )}

          {step === "error" && (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>⚠️</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#DC2626", marginBottom: 6 }}>Invalid Invite</div>
              <div style={{ fontSize: 12, color: "#555" }}>{error}</div>
              <button onClick={() => window.location.href = "/"} style={{ ...btn(), marginTop: 20 }}>
                Go to RevXcel
              </button>
            </div>
          )}

          {step === "review" && invite && (
            <div>
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#111", marginBottom: 4 }}>
                  You've been invited to {invite.org_name}
                </div>
                <div style={{ fontSize: 12, color: "#555" }}>
                  as a <strong>{ROLE_LABELS[invite.role] || invite.role}</strong>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <button onClick={() => setStep("signup")} style={btn()}>
                  Create a new account
                </button>
                <button onClick={() => setStep("login")}
                  style={{ ...btn("transparent"), color: "#CC1A1A", border: "1px solid rgba(204,26,26,0.25)",
                    background: "rgba(204,26,26,0.04)", marginTop: 0 }}>
                  I already have an account
                </button>
              </div>
            </div>
          )}

          {step === "signup" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 4 }}>
                Create your account
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 5 }}>FULL NAME</label>
                <input style={inp} placeholder="Jane Smith" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 5 }}>EMAIL</label>
                <input style={{ ...inp, background: "#f0f0f0", color: "#aaa" }} value={email} readOnly />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 5 }}>PASSWORD</label>
                <input style={inp} type="password" placeholder="Min. 8 characters" value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              {error && <div style={{ padding: "8px 12px", background: "rgba(220,38,38,0.06)", borderRadius: 7, fontSize: 12, color: "#DC2626" }}>{error}</div>}
              <button onClick={acceptWithNewAccount} disabled={working} style={btn()}>
                {working ? "Creating account…" : "Create Account & Join"}
              </button>
              <button onClick={() => setStep("review")}
                style={{ background: "none", border: "none", fontSize: 12, color: "#aaa", cursor: "pointer", marginTop: 4 }}>
                ← Back
              </button>
            </div>
          )}

          {step === "login" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 4 }}>
                Sign in to accept
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 5 }}>EMAIL</label>
                <input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 5 }}>PASSWORD</label>
                <input style={inp} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
              </div>
              {error && <div style={{ padding: "8px 12px", background: "rgba(220,38,38,0.06)", borderRadius: 7, fontSize: 12, color: "#DC2626" }}>{error}</div>}
              <button onClick={acceptWithExistingAccount} disabled={working} style={btn()}>
                {working ? "Signing in…" : "Sign In & Join"}
              </button>
              <button onClick={() => setStep("review")}
                style={{ background: "none", border: "none", fontSize: 12, color: "#aaa", cursor: "pointer", marginTop: 4 }}>
                ← Back
              </button>
            </div>
          )}

          {step === "done" && (
            <div style={{ textAlign: "center", padding: "10px 0" }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#16A34A", marginBottom: 6 }}>You're in!</div>
              <div style={{ fontSize: 12, color: "#555" }}>Redirecting to RevXcel…</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
